import {
  ArrowLeftIcon,
  Award01Icon,
  Calendar02Icon,
  GroupIcon,
  Loading01Icon,
  Mail01Icon,
  Shield01Icon,
  Ticket01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { format, formatDistanceToNow } from 'date-fns';
import { useMemo } from 'react';
import type { User, UserQualification } from 'shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UserAvatar } from '@/components/ui/user-avatar';
import { maintenanceTicketsApi, teamsApi } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { z } from 'zod';

type TimePreset = '24h' | '7d' | '30d' | 'custom';

const userDetailSearchSchema = z.object({
  preset: z.enum(['24h', '7d', '30d', 'custom']).optional(),
  tab: z.enum(['overview', 'activity']).optional(),
  startDate: z.string().optional(),
  startTime: z.string().optional(),
  endDate: z.string().optional(),
  endTime: z.string().optional(),
  from: z.enum(['profile', 'teams']).optional(),
});

type UserDetailSearch = z.infer<typeof userDetailSearchSchema>;

function useTimeRange(
  preset: TimePreset,
  customStart: string,
  customStartTime: string,
  customEnd: string,
  customEndTime: string,
) {
  return useMemo(() => {
    const now = new Date();
    if (preset === 'custom') {
      const start = customStart
        ? new Date(`${customStart}T${customStartTime}:00`).toISOString()
        : undefined;
      const end = customEnd
        ? new Date(`${customEnd}T${customEndTime}:59`).toISOString()
        : undefined;
      return { start, end };
    }
    const d = new Date();
    if (preset === '24h') d.setHours(d.getHours() - 24);
    else if (preset === '7d') d.setDate(d.getDate() - 7);
    else if (preset === '30d') d.setDate(d.getDate() - 30);
    return { start: d.toISOString(), end: now.toISOString() };
  }, [preset, customStart, customStartTime, customEnd, customEndTime]);
}

interface UserWithTeamData extends User {
  onShift?: boolean;
  effort?: {
    incompleteTicketCount: number;
    priorities?: string[];
    tickets?: Array<{
      id: string;
      title: string;
      ticketNumber: string;
      priority: string;
      priorityColor: string | null;
      asset: {
        id: string;
        name: string;
        tagNumber: string | null;
        image: string | null;
      } | null;
      status: string | null;
    }>;
  };
  qualifications?: UserQualification[];
  groups?: Array<{ id: string; name: string; color: string }>;
  gamification?: {
    level?: { level: number; name?: string } | null;
    totalPoints?: number;
    badges?: Array<{ id: string; name: string; icon?: string; earnedAt?: string }>;
  };
}

function UserDetailPage() {
  const { userId } = Route.useParams();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();

  const preset: TimePreset = search.preset ?? '7d';
  const activeTab = search.tab ?? 'overview';
  const customStart = search.startDate ?? '';
  const customStartTime = search.startTime ?? '00:00';
  const customEnd = search.endDate ?? '';
  const customEndTime = search.endTime ?? '23:59';
  const fromPage = search.from ?? 'teams';

  const setSearch = (patch: Partial<UserDetailSearch>) => {
    navigate({ search: (prev: UserDetailSearch) => ({ ...prev, ...patch }) });
  };

  const timeRange = useTimeRange(preset, customStart, customStartTime, customEnd, customEndTime);

  const {
    data: userData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['user-detail', userId],
    queryFn: async () => {
      const now = new Date();
      const clientDate = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}`;
      const clientTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

      const res = await teamsApi.getAll({
        page: 1,
        limit: 50,
        includeEffort: true,
        prioritizeShift: true,
        clientDate,
        clientTime,
        includeQualifications: true,
        includeGroups: true,
        includeGamification: true,
        includeTickets: true,
      });

      const users = Array.isArray(res) ? res : ((res as any)?.data ?? []);
      const user = users.find((u: any) => u.id === userId);
      if (!user) return null;
      return user as UserWithTeamData;
    },
  });

  const { data: ticketsData, isLoading: ticketsLoading } = useQuery({
    queryKey: [
      'user-tickets',
      { assignedTo: userId, startDate: timeRange.start, endDate: timeRange.end, limit: 50 },
    ],
    queryFn: () =>
      maintenanceTicketsApi.getAll({
        assignedTo: userId,
        startDate: timeRange.start,
        endDate: timeRange.end,
        dateField: 'createdAt',
        limit: 50,
      }),
  });

  const tickets = ticketsData?.data ?? [];
  const user = userData as UserWithTeamData | undefined;
  const qualifications = user?.qualifications ?? [];
  const groups = user?.groups ?? [];
  const gamification = user?.gamification;
  const level = gamification?.level;
  const points = gamification?.totalPoints ?? 0;
  const badges = (gamification?.badges ?? []) as Array<{
    id: string;
    name: string;
    icon?: string;
    earnedAt?: string;
  }>;

  const navigateBack = () => {
    if (fromPage === 'profile') {
      navigate({ to: '/home' as any });
    } else {
      navigate({
        to: '/teams' as any,
        search: {
          search: undefined,
          groups: undefined,
          sort: 'shift',
          order: 'desc',
          view: 'card',
        },
      } as any);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <HugeiconsIcon
          icon={Loading01Icon}
          className="w-8 h-8 animate-spin text-muted-foreground/40"
        />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Failed to load user details</p>
          <Button variant="outline" size="sm" onClick={navigateBack} className="gap-2">
            <HugeiconsIcon icon={ArrowLeftIcon} className="w-4 h-4" />
            Back to Teams
          </Button>
        </div>
      </div>
    );
  }

  const onShift = user.onShift;

  const backLabel = fromPage === 'profile' ? 'Home' : 'Teams';

  return (
    <div className="flex flex-col gap-3 h-[calc(100dvh-7.5rem)] overflow-hidden">
      {/* Hero Header */}
      <div className="relative shrink-0 rounded-xl overflow-hidden border border-border/50">
        <div
          className={cn(
            'w-full h-28 sm:h-40',
            'bg-gradient-to-br',
            onShift
              ? 'from-emerald-500/10 via-muted/30 to-muted'
              : 'from-slate-500/10 via-muted/30 to-muted',
          )}
        />

        {/* Content overlay */}
        <div className="absolute inset-0 flex flex-col justify-between p-4">
          {/* Top row: nav */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={navigateBack}
              className="gap-1.5 text-foreground/80 hover:text-foreground shrink-0 h-7 px-2 bg-background/40 backdrop-blur-sm"
            >
              <HugeiconsIcon icon={ArrowLeftIcon} className="w-4 h-4" />
              <span className="text-xs">{backLabel}</span>
            </Button>
          </div>

          {/* Bottom row: user info */}
          <div className="flex items-end justify-between gap-4">
            <div className="min-w-0 flex items-end gap-3">
              <UserAvatar
                user={user}
                showName={false}
                size="lg"
                avatarClassName="w-10 h-10 sm:w-14 sm:h-14 ring-2 ring-background shadow-lg"
              />
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h1 className="text-base sm:text-xl font-bold truncate">{user.name}</h1>
                  <div
                    className={cn(
                      'flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-border/50 bg-background/60 backdrop-blur-sm shrink-0',
                    )}
                  >
                    <span
                      className={cn(
                        'w-1.5 h-1.5 rounded-full',
                        onShift ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400',
                      )}
                    />
                    <span
                      className={cn(
                        'text-[10px] font-semibold uppercase tracking-wide',
                        onShift
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-slate-500 dark:text-slate-400',
                      )}
                    >
                      {onShift ? 'On Shift' : 'Off Shift'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-3 text-[11px] sm:text-xs text-muted-foreground flex-wrap">
                  <span className="font-mono tracking-tight">@{user.username}</span>
                  <span className="text-muted-foreground/30">·</span>
                  <span className="flex items-center gap-1 truncate max-w-[180px] sm:max-w-none">
                    <HugeiconsIcon icon={Mail01Icon} className="w-3 h-3 shrink-0" />
                    <span className="truncate">{user.email}</span>
                  </span>
                  {level && (
                    <>
                      <span className="text-muted-foreground/30 hidden sm:inline">·</span>
                      <span className="hidden sm:inline items-center gap-1">
                        <HugeiconsIcon icon={Shield01Icon} className="w-3 h-3" />
                        Lv.{level.level} {level.name && `(${level.name})`}
                      </span>
                    </>
                  )}
                  {points > 0 && (
                    <>
                      <span className="text-muted-foreground/30 hidden sm:inline">·</span>
                      <span className="hidden sm:inline">{points.toLocaleString()} pts</span>
                    </>
                  )}
                </div>
                {/* Mobile: compact level/points + group dots */}
                <div className="flex items-center gap-2 mt-1 sm:hidden">
                  {level && (
                    <span className="text-[10px] text-muted-foreground">Lv.{level.level}</span>
                  )}
                  {points > 0 && (
                    <span className="text-[10px] text-muted-foreground">
                      {points.toLocaleString()} pts
                    </span>
                  )}
                  {groups.length > 0 && (
                    <div className="flex items-center gap-1 ml-auto">
                      {groups.slice(0, 4).map((g) => (
                        <span
                          key={g.id}
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ backgroundColor: g.color }}
                          title={g.name}
                        />
                      ))}
                      {groups.length > 4 && (
                        <span className="text-[10px] text-muted-foreground">
                          +{groups.length - 4}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Desktop group tags */}
            {groups.length > 0 && (
              <div className="hidden md:flex items-center gap-1.5 shrink-0">
                {groups.slice(0, 3).map((g) => (
                  <Badge
                    key={g.id}
                    variant="outline"
                    className="text-[10px] bg-background/60 backdrop-blur-sm"
                    style={{ borderColor: g.color }}
                  >
                    {g.name}
                  </Badge>
                ))}
                {groups.length > 3 && (
                  <Badge
                    variant="outline"
                    className="text-[10px] bg-background/60 backdrop-blur-sm"
                  >
                    +{groups.length - 3}
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Time Range Filter */}
      <div className="space-y-2 shrink-0">
        <div className="flex items-center gap-1.5 sm:gap-2">
          {(['24h', '7d', '30d'] as TimePreset[]).map((p) => (
            <Button
              key={p}
              variant={preset === p ? 'default' : 'outline'}
              size="sm"
              className="h-8 sm:h-7 px-2.5 sm:px-2.5 text-xs"
              onClick={() => setSearch({ preset: p })}
            >
              <span className="sm:hidden">{p}</span>
              <span className="hidden sm:inline">
                {p === '24h' ? 'Last 24h' : p === '7d' ? 'Last 7d' : 'Last 30d'}
              </span>
            </Button>
          ))}
          <Button
            variant={preset === 'custom' ? 'default' : 'outline'}
            size="sm"
            className="h-8 sm:h-7 px-2.5 sm:px-2.5 text-xs"
            onClick={() => {
              const patch: Partial<UserDetailSearch> = { preset: 'custom' };
              if (!customStart) {
                const d = new Date();
                d.setDate(d.getDate() - 7);
                patch.startDate = d.toISOString().split('T')[0];
                patch.endDate = new Date().toISOString().split('T')[0];
              }
              setSearch(patch);
            }}
          >
            Custom
          </Button>
        </div>
        {preset === 'custom' && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Input
                type="date"
                value={customStart}
                onChange={(e) => setSearch({ startDate: e.target.value })}
                className="h-8 sm:h-7 flex-1 min-w-0 text-xs"
              />
              <Input
                type="time"
                value={customStartTime}
                onChange={(e) => setSearch({ startTime: e.target.value })}
                className="h-8 sm:h-7 w-[100px] sm:w-[100px] text-xs"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <Input
                type="date"
                value={customEnd}
                onChange={(e) => setSearch({ endDate: e.target.value })}
                className="h-8 sm:h-7 flex-1 min-w-0 text-xs"
              />
              <Input
                type="time"
                value={customEndTime}
                onChange={(e) => setSearch({ endTime: e.target.value })}
                className="h-8 sm:h-7 w-[100px] sm:w-[100px] text-xs"
              />
            </div>
          </div>
        )}
      </div>

      {/* Content Section */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => setSearch({ tab: v as 'overview' | 'activity' })}
        className="flex-1 min-h-0 flex flex-col"
      >
        <TabsList className="shrink-0 w-full">
          <TabsTrigger value="overview" className="flex-1 text-xs">
            Overview
          </TabsTrigger>
          <TabsTrigger value="activity" className="flex-1 text-xs">
            Activity
          </TabsTrigger>
        </TabsList>

        {/* Overview: 3-column on desktop, nested tabs on mobile */}
        <TabsContent value="overview" className="flex-1 min-h-0 mt-2">
          <div className="hidden lg:grid lg:grid-cols-3 lg:h-full gap-3">
            <ScrollableCard
              title="Assigned Tickets"
              icon={<HugeiconsIcon icon={Ticket01Icon} className="w-4 h-4 text-primary" />}
              iconBg="bg-primary/10"
              count={ticketsData?.pagination?.totalItems ?? tickets.length}
              loading={ticketsLoading}
            >
              <TicketList tickets={tickets} />
            </ScrollableCard>

            <ScrollableCard
              title="Qualifications"
              icon={
                <HugeiconsIcon
                  icon={Shield01Icon}
                  className="w-4 h-4 text-blue-600 dark:text-blue-400"
                />
              }
              iconBg="bg-blue-500/10"
              count={qualifications.length}
              loading={false}
            >
              <QualificationsList qualifications={qualifications} />
            </ScrollableCard>

            <ScrollableCard
              title="Groups & Badges"
              icon={
                <HugeiconsIcon
                  icon={Award01Icon}
                  className="w-4 h-4 text-emerald-600 dark:text-emerald-400"
                />
              }
              iconBg="bg-emerald-500/10"
              count={groups.length + badges.length}
              loading={false}
            >
              <GroupsBadgesList groups={groups} badges={badges} level={level} points={points} />
            </ScrollableCard>
          </div>

          {/* Mobile: vertically stacked scrollable cards */}
          <div className="flex flex-col gap-2.5 overflow-y-auto lg:hidden h-full scrollbar-hide">
            <MobileSectionCard
              title="Assigned Tickets"
              icon={<HugeiconsIcon icon={Ticket01Icon} className="w-4 h-4 text-primary" />}
              iconBg="bg-primary/10"
              count={ticketsData?.pagination?.totalItems ?? tickets.length}
            >
              {ticketsLoading ? <LoadingSpinner /> : <TicketList tickets={tickets} />}
            </MobileSectionCard>

            <MobileSectionCard
              title="Qualifications"
              icon={
                <HugeiconsIcon
                  icon={Shield01Icon}
                  className="w-4 h-4 text-blue-600 dark:text-blue-400"
                />
              }
              iconBg="bg-blue-500/10"
              count={qualifications.length}
            >
              <QualificationsList qualifications={qualifications} />
            </MobileSectionCard>

            <MobileSectionCard
              title="Groups & Badges"
              icon={
                <HugeiconsIcon
                  icon={Award01Icon}
                  className="w-4 h-4 text-emerald-600 dark:text-emerald-400"
                />
              }
              iconBg="bg-emerald-500/10"
              count={groups.length + badges.length}
            >
              <GroupsBadgesList groups={groups} badges={badges} level={level} points={points} />
            </MobileSectionCard>
          </div>
        </TabsContent>

        {/* Activity tab placeholder */}
        <TabsContent value="activity" className="flex-1 min-h-0 mt-2">
          <Card className="h-full border-muted/50 bg-card/30 flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <HugeiconsIcon
                icon={Calendar02Icon}
                className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30"
              />
              <p className="text-sm font-medium">Activity Tracking</p>
              <p className="text-xs mt-1">
                Coming soon — ticket completion trends and activity metrics
              </p>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ── Scrollable Card ─────────────────────────────────────────────── */

function MobileSectionCard({
  title,
  icon,
  iconBg,
  count,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  iconBg: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <Card className="border-muted/50 bg-card/30">
      <CardHeader className="pb-2 pt-3 px-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          <div className={cn('w-6 h-6 rounded-md flex items-center justify-center', iconBg)}>
            {icon}
          </div>
          {title}
          <Badge variant="outline" className="text-[10px] ml-auto">
            {count}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 pb-3">{children}</CardContent>
    </Card>
  );
}

/* ── Desktop Scrollable Card ─────────────────────────────────────────────── */

function ScrollableCard({
  title,
  icon,
  iconBg,
  count,
  loading,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  iconBg: string;
  count: number;
  loading: boolean;
  children: React.ReactNode;
}) {
  return (
    <Card className="border-muted/50 bg-card/30 backdrop-blur-sm overflow-hidden flex flex-col">
      <CardHeader className="pb-2 pt-3 px-3 shrink-0">
        <CardTitle className="flex items-center gap-2 text-sm">
          <div className={cn('w-6 h-6 rounded-md flex items-center justify-center', iconBg)}>
            {icon}
          </div>
          {title}
          <Badge variant="outline" className="text-[10px] ml-auto">
            {count}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 min-h-0 px-0 pb-0">
        <ScrollArea className="h-full">
          <div className="px-3 pb-3">{loading ? <LoadingSpinner /> : children}</div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

/* ── Ticket List ─────────────────────────────────────────────────── */

function TicketList({ tickets }: { tickets: any[] }) {
  const navigate = useNavigate();
  if (tickets.length === 0) {
    return <EmptyState icon={Ticket01Icon} text="No assigned tickets in this period" />;
  }
  return (
    <div className="space-y-1.5">
      {tickets.map((ticket) => (
        <button
          type="button"
          key={ticket.id}
          className="flex items-start gap-2.5 p-2 rounded-lg border border-border/40 hover:bg-accent/40 transition-colors text-left w-full"
          onClick={() =>
            navigate({
              to: '/tickets/$ticketId',
              params: { ticketId: ticket.id },
            })
          }
        >
          <div
            className={cn(
              'w-1.5 h-1.5 rounded-full mt-1.5 shrink-0',
              ticket.status === 'completed' && 'bg-emerald-500',
              ticket.status === 'in_progress' && 'bg-amber-500',
              ticket.status === 'open' && 'bg-rose-500',
              !['completed', 'in_progress', 'open'].includes(ticket.status) && 'bg-muted',
            )}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs font-medium truncate">{ticket.title}</p>
              <Badge
                variant="outline"
                className={cn(
                  'text-[9px] capitalize shrink-0 px-1.5 py-0',
                  ticket.status === 'completed' &&
                    'border-emerald-500/30 text-emerald-600 dark:text-emerald-400',
                  ticket.status === 'in_progress' &&
                    'border-amber-500/30 text-amber-600 dark:text-amber-400',
                  ticket.status === 'open' && 'border-rose-500/30 text-rose-600 dark:text-rose-400',
                )}
              >
                {ticket.status?.replace('_', ' ') || 'open'}
              </Badge>
            </div>
            <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground">
              {ticket.createdAt && (
                <span>{formatDistanceToNow(new Date(ticket.createdAt), { addSuffix: true })}</span>
              )}
              {ticket.asset && (
                <>
                  <span>·</span>
                  <span>{ticket.asset.name}</span>
                </>
              )}
              {ticket.priority && (
                <>
                  <span>·</span>
                  <span className="capitalize">{ticket.priority}</span>
                </>
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

/* ── Qualifications List ─────────────────────────────────────────── */

function QualificationsList({ qualifications }: { qualifications: UserQualification[] }) {
  if (qualifications.length === 0) {
    return <EmptyState icon={Shield01Icon} text="No qualifications recorded" />;
  }
  return (
    <div className="space-y-1.5">
      {qualifications.map((q) => {
        const qual = q.qualification;
        const statusColor =
          q.status === 'active'
            ? 'text-emerald-600 dark:text-emerald-400'
            : q.status === 'expired'
              ? 'text-rose-600 dark:text-rose-400'
              : 'text-amber-600 dark:text-amber-400';
        const statusBg =
          q.status === 'active'
            ? 'bg-emerald-500/10 border-emerald-200/50 dark:border-emerald-800/50'
            : q.status === 'expired'
              ? 'bg-rose-500/10 border-rose-200/50 dark:border-rose-800/50'
              : 'bg-amber-500/10 border-amber-200/50 dark:border-amber-800/50';

        return (
          <div
            key={q.id}
            className="flex items-center gap-2.5 p-2 rounded-lg border border-border/40"
          >
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">
                {qual?.name || 'Unknown Qualification'}
              </p>
              <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground">
                {q.awardedAt && <span>Awarded {format(new Date(q.awardedAt), 'MMM d, yyyy')}</span>}
                {q.awardedBy && (
                  <>
                    <span>·</span>
                    <span>by {q.awardedBy}</span>
                  </>
                )}
              </div>
            </div>
            <Badge
              variant="outline"
              className={cn('text-[9px] capitalize shrink-0 px-1.5 py-0', statusColor, statusBg)}
            >
              {q.status || 'active'}
            </Badge>
            {q.expiresAt && (
              <span className="text-[10px] text-muted-foreground shrink-0">
                Exp. {format(new Date(q.expiresAt), 'MMM d')}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Groups & Badges List ────────────────────────────────────────── */

function GroupsBadgesList({
  groups,
  badges,
  level,
  points,
}: {
  groups: Array<{ id: string; name: string; color: string }>;
  badges: Array<{ id: string; name: string; icon?: string; earnedAt?: string }>;
  level?: { level: number; name?: string } | null;
  points: number;
}) {
  return (
    <div className="space-y-4">
      {/* Level & Points */}
      {(level || points > 0) && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
            Gamification
          </p>
          <div className="flex items-center gap-3 p-2 rounded-lg bg-muted/30 border border-border/30">
            {level && (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                  {level.level}
                </div>
                <div>
                  <p className="text-xs font-medium">Level {level.level}</p>
                  {level.name && <p className="text-[10px] text-muted-foreground">{level.name}</p>}
                </div>
              </div>
            )}
            {points > 0 && (
              <div className="text-right ml-auto">
                <p className="text-sm font-bold">{points.toLocaleString()}</p>
                <p className="text-[10px] text-muted-foreground">points</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Groups */}
      {groups.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
            Groups
          </p>
          <div className="space-y-1">
            {groups.map((g) => (
              <div
                key={g.id}
                className="flex items-center gap-2 p-2 rounded-lg border border-border/40"
              >
                <div
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: g.color }}
                />
                <span className="text-xs font-medium">{g.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Badges */}
      {badges.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
            Badges ({badges.length})
          </p>
          <div className="space-y-1">
            {badges.map((b) => (
              <div
                key={b.id}
                className="flex items-center gap-2 p-2 rounded-lg border border-border/40"
              >
                <HugeiconsIcon icon={Award01Icon} className="w-4 h-4 text-amber-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{b.name}</p>
                  {b.earnedAt && (
                    <p className="text-[10px] text-muted-foreground">
                      {format(new Date(b.earnedAt), 'MMM d, yyyy')}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {groups.length === 0 && badges.length === 0 && !level && points === 0 && (
        <EmptyState icon={GroupIcon} text="No groups or badges yet" />
      )}
    </div>
  );
}

/* ── Shared Helpers ──────────────────────────────────────────────── */

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-8">
      <HugeiconsIcon
        icon={Loading01Icon}
        className="w-5 h-5 animate-spin text-muted-foreground/40"
      />
    </div>
  );
}

function EmptyState({ icon, text }: { icon: any; text: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
      <HugeiconsIcon icon={icon} className="w-8 h-8 mb-2 text-muted-foreground/30" />
      <p className="text-xs">{text}</p>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/user/$userId')({
  component: UserDetailPage,
  validateSearch: (search): UserDetailSearch => userDetailSearchSchema.parse(search),
});
